// Small shared helpers for the submission surfaces (worker drawer + the
// per-slot cards it renders). Extracted so the slot components do not have to
// import from the drawer that renders them.
import { mutate } from 'swr';
import { ApiError } from './api';

/**
 * The server's own message, when it sent one — never a generic swallow.
 *
 * `missing: [...]` (addtext's SLOT_INPUT_REQUIRED, relayed by the console) is
 * appended when the message does not already name the fields: "Add text failed"
 * does not tell a worker that the ink was the problem.
 */
export function errMsg(e: unknown, fallback: string): string {
  const body = (e as ApiError).body as { message?: string; error?: string; missing?: unknown } | null;
  const base = body?.message || body?.error || fallback;
  const missing = Array.isArray(body?.missing)
    ? body.missing.filter((m): m is string => typeof m === 'string' && m.trim() !== '')
    : [];
  if (!missing.length) return base;
  const detail = `missing: ${missing.join(', ')}`;
  return base.includes(detail) ? base : `${base} (${detail})`;
}

/**
 * Re-read the submission after every mutation so badges, thumbnails and the
 * list row all reflect what actually landed in Firestore (rather than what the
 * response optimistically claimed).
 */
export async function refreshSubmission(id: string): Promise<void> {
  await Promise.all([
    mutate(`/api/submissions/${id}`),
    mutate((k: unknown) => typeof k === 'string' && k.startsWith('/api/submissions?'), undefined, { revalidate: true }),
    mutate((k: unknown) => typeof k === 'string' && k.startsWith('/api/revisions'), undefined, { revalidate: true }),
  ]);
}

/** Multipart POST that carries the console's CSRF header. */
export async function postMultipart<T>(url: string, form: FormData): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    body: form,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

/**
 * The window.prompt() text shown when generateproof's consistency gate
 * refuses a send (409 PROOF_CONSISTENCY_FAILED). Typing a reason of 3+ chars
 * is what the console forwards as `consistencyOverride:{reason}`.
 */
export function consistencyPromptText(findings: Array<{ detail?: string; id?: string }> = []): string {
  const list = findings.map(f => `• ${f.detail || f.id || 'Unnamed problem'}`).join('\n');
  return `This proof would go out with these problems:\n\n${list}\n\nTo send it anyway, type why (saved on the submission). Cancel to fix it first.`;
}
